import React, { useState } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

interface FavoriteButtonProps {
  isFavorited: boolean;
  onToggleFavorite: () => Promise<void> | void;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  isFavorited,
  onToggleFavorite,
  size = 'small',
  className = ''
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isLoading) return; // Prevent double-clicks

    setIsAnimating(true);
    setIsLoading(true);

    try {
      await onToggleFavorite();
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsLoading(false);
      // Reset animation state after animation completes
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }
  };

  const iconSize = size === 'small' ? 18 : size === 'medium' ? 20 : 24;

  return (
    <Tooltip title={isFavorited ? 'Remove from favorites' : 'Add to favorites'} arrow>
      <IconButton
        size={size}
        onClick={handleClick}
        disabled={isLoading}
        className={className}
        sx={{
          transition: 'transform 0.15s ease, color 0.15s ease',
          transform: isAnimating ? 'scale(1.2)' : 'scale(1)',
          color: isFavorited ? '#ffd700' : 'action.active',
          '&:hover': {
            color: isFavorited ? '#ffed4e' : '#ffd700',
            transform: 'scale(1.1)',
          },
          '&.Mui-disabled': {
            opacity: 0.6,
          },
        }}
      >
        {isLoading ? (
          <CircularProgress size={iconSize} />
        ) : isFavorited ? (
          <StarIcon sx={{ fontSize: iconSize }} />
        ) : (
          <StarBorderIcon sx={{ fontSize: iconSize }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

export default FavoriteButton;
